import React, { useEffect, useRef } from "react"

import { useForm } from "react-hook-form"
import { Link, useNavigate } from "react-router-dom"

import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { InputElement } from "@front-shared/elements/input.element"
import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element"
import { PasswordStrengthElement } from "@front-shared/elements/passwordStrength.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { ToastrHelper } from "@front-shared/helpers/toastr.helper"
import { useIsMobile } from "@front-shared/hooks/isMobile.hook"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook"

import { GENERAL_CONFIG } from "../../config/general.config"
import { ROUTES_CONFIG } from "../../config/routes.config"
import { useAppDispatch } from "../../store/appDispatch.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { authSelector } from "../../store/selectors/auth.selector"
import { authReset } from "../../store/slices/auth.slice"
import { authForgotPasswordProcess } from "../../store/thunks/auth.thunks"

import arrowRightIcon from "@front-shared/assets/images/icons/arrow-right2.svg"
import mailIcon from "@front-shared/assets/images/icons/mail-dark.svg"
import verificationCodeIcon from "@front-shared/assets/images/icons/verification-code-dark.svg"

export const RestoreProcessComponent: React.FunctionComponent = () => {
    useScrollFix()

    const isMobile = useIsMobile()

    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const authState = useAppSelector(authSelector)

    const {
        handleSubmit,
        formState: { errors },
        watch,
        setValue,
        control,
    } = useForm()

    const password: any = useRef({})
    password.current = watch("password", "")

    useEffect(() => {
        if (!authState.forgotPasswordEmail) {
            ToastrHelper.error("Not enough data to proceed. Please try again")

            navigate(ROUTES_CONFIG.loginUrl)

            return
        }

        return () => {
            dispatch(authReset())
        }
    }, [])

    useEffect(() => {
        if (!authState.forgotPasswordEmail) {
            return
        }

        setValue("email", authState.forgotPasswordEmail)
    }, [authState.forgotPasswordEmail])

    const onFormSubmit = (data: any) => {
        if (!authState.forgotPasswordEmail) {
            return
        }

        dispatch(
            authForgotPasswordProcess({
                payload: data,
                navigate,
            }),
        )
    }

    return (
        <div className="md:px-[16px]">
            <PageHelmetElement
                title="Process Restore Password"
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            <PrimaryText className="md:mt-[32px]" typography="h1" centered>
                Change your password
            </PrimaryText>

            <form onSubmit={handleSubmit(onFormSubmit)} noValidate={true}>
                <div className="flex items-end sm:block">
                    <div className="flex-1 mr-[27px] sm:mr-[0px]">
                        <InputElement
                            className="mt-[44px]"
                            name="email"
                            label="Email"
                            disabled
                            reactHookControl={control}
                            reactHookErrors={errors}
                            reactHookValidations={{
                                required: FRONT_VALIDATION_CONFIG.required,
                                pattern: FRONT_VALIDATION_CONFIG.email,
                                maxLength: FRONT_VALIDATION_CONFIG.maxLength,
                            }}
                            defaultValue={authState.forgotPasswordEmail}
                            inputSuffix={
                                <img
                                    src={mailIcon}
                                    alt="Email address"
                                    title="Email address"
                                    className="ml-[8px]"
                                />
                            }
                        />

                        <InputElement
                            className="mt-[44px]"
                            name="verificationCode"
                            label="Code"
                            placeholder="Enter your code"
                            reactHookControl={control}
                            reactHookErrors={errors}
                            reactHookValidations={{
                                required: FRONT_VALIDATION_CONFIG.required,
                            }}
                            inputSuffix={
                                <img
                                    src={verificationCodeIcon}
                                    alt="Verification code"
                                    title="Verification code"
                                    className="ml-[8px]"
                                />
                            }
                        />

                        <PrimaryText
                            className="mt-[4px]"
                            size={10}
                            lineHeight={20}
                        >
                            Check your email for your verification code
                        </PrimaryText>

                        <InputElement
                            className="mt-[44px]"
                            name="password"
                            type="password"
                            label="Set password"
                            reactHookControl={control}
                            reactHookErrors={errors}
                            reactHookValidations={{
                                required: FRONT_VALIDATION_CONFIG.required,
                                validate:
                                    FRONT_VALIDATION_CONFIG.passwordSpecialValidations,
                            }}
                        />
                    </div>

                    <PasswordStrengthElement
                        className="sm:mt-[32px]"
                        width={isMobile ? undefined : 260}
                        currentValue={password.current}
                        trianglePlacement={isMobile ? undefined : "bottomLeft"}
                    />
                </div>

                <div className="mt-[32px] flex items-center">
                    <div className="flex-1 mr-[10px]">
                        <Link to={ROUTES_CONFIG.loginUrl}>Cancel</Link>
                    </div>

                    <ButtonElement
                        label="Change password"
                        type="primary"
                        htmlType="submit"
                        size="large"
                        icon={arrowRightIcon}
                        isLoading={authState.isLoading}
                    />
                </div>
            </form>
        </div>
    )
}
